import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  fetchProjectDetailByCode,
  ProjectDetail,
  updateProjectInformationDelta,
} from '../../../../infrastructure/api/project'
import { fromDetail, toDeltaInput } from '../model'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { APPLICATION_FUNCTION_EXTERNAL_ID } from '../..'
import { useProjectFormState } from './useProjectFormState'

// プロジェクト情報画面（プロジェクトメニュー）の data フック。
// 更新のみ（作成・削除・isCreating の概念は持たない）。更新先は /projects/information/delta。
// NOTE: APPLICATION_FUNCTION_EXTERNAL_ID はモジュールトップレベルで読むと
// pages/index.tsx との循環参照で初期化前アクセス（undefined）になるため、
// 必ず実行時（コールバック内）で参照する。
export const useProjectInformationSingleSheetData = (code: string) => {
  const dispatch = useDispatch()
  const [initialized, setInitialized] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [entity, setEntity] = useState<ProjectDetail | undefined>()
  const {
    formModel,
    setFormModel,
    formModelRef,
    onChangeProject,
    edited,
    refreshedPageData,
    editedFormData,
    refreshEditedFormData,
  } = useProjectFormState()

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    try {
      const detail = await fetchProjectDetailByCode(code)
      setEntity(detail)
      setFormModel(fromDetail(detail))
    } finally {
      setInitialized(true)
      setIsLoading(false)
      refreshedPageData()
      refreshEditedFormData()
    }
  }, [code, setFormModel, refreshedPageData, refreshEditedFormData])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const submit = useCallback(async () => {
    const currentFormModel = formModelRef.current
    if (!entity) return
    setIsLoading(true)
    try {
      await updateProjectInformationDelta(
        toDeltaInput(entity, currentFormModel)
      )
      dispatch(
        addScreenMessage(
          APPLICATION_FUNCTION_EXTERNAL_ID.PROJECT_INFORMATION_REFERENCE,
          {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'update.completed.title' }),
          }
        )
      )
      await fetchData()
    } finally {
      setIsLoading(false)
    }
  }, [entity, fetchData, dispatch, formModelRef])

  return {
    initialized,
    // 情報画面は常に更新（作成モードは存在しない）。
    isCreating: false,
    isLoading,
    edited,
    editedFormData,
    formModel,
    entity,
    onChangeProject,
    submit,
    reload: fetchData,
  }
}
